import { createContext, useEffect, useState, ReactNode } from 'react';
import { useApolloClient } from '@apollo/client';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Cookies from 'js-cookie';
import { QUERY_TRANSLATE } from '@/graphql/queries/translate';
import type { Translate } from '@/graphql/types/translate';
import { normalizeTranslateData } from '@/utils/functions/translate';
import enUS from '@/locales/en-US.json';
import ptBR from '@/locales/pt-BR.json';

type Language = 'en-US' | 'pt-BR';

interface LanguageContextType {
  currentLanguage: Language;
  loading: boolean;
  setLanguage: (lang: Language) => Promise<void>;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_COOKIE_KEY = 'kyt_lang_pref';

const DOMAIN_HOSTNAME = import.meta.env.VITE_DOMAIN_HOSTNAME as string;

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const client = useApolloClient();

  const getSavedLanguage = (): Language => {
    const savedLanguage = Cookies.get(LANGUAGE_COOKIE_KEY) as Language;
    return savedLanguage || 'en-US';
  };

  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    return getSavedLanguage();
  });
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);

  const saveLanguage = (lang: Language): void => {
    const cookieOptions: Cookies.CookieAttributes = {
      expires: 365,
      path: '/',
      sameSite: 'lax',
      secure: true,
      domain: DOMAIN_HOSTNAME,
    };

    Cookies.set(LANGUAGE_COOKIE_KEY, lang, cookieOptions);
  };

  useEffect(() => {
    const lang = getSavedLanguage();
    i18n
      .use(initReactI18next)
      .init({
        resources: {
          'en-US': { translation: enUS },
          'pt-BR': { translation: ptBR },
        },
        lng: lang,
        fallbackLng: 'en-US',
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
      })
      .finally(() => setInitialized(true));
  }, []);

  const loadTranslations = async (lang: Language) => {
    try {
      const { data } = await client.query<{ staticTranslations: Translate }>({
        query: QUERY_TRANSLATE,
        variables: { language: lang },
        fetchPolicy: 'network-only',
      });

      const freshTranslations = normalizeTranslateData(data.staticTranslations);

      i18n.addResourceBundle(lang, 'translation', freshTranslations, true, true);
      i18n.changeLanguage(lang);
      setCurrentLanguage(lang);
    } finally {
      setLoading(false);
    }
  };

  const setLanguage = async (lang: Language) => {
    saveLanguage(lang);
    await loadTranslations(lang);
  };

  useEffect(() => {
    if (initialized) {
      const lang = getSavedLanguage();
      loadTranslations(lang);
    }
  }, [initialized]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, loading }}>
      {children}
    </LanguageContext.Provider>
  );
};
